import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

export const TYPE_ID_MAX_LEN = 3;
export const TYPE_ID_MIN_LEN = 2;

export const MASTER_TYPES = [
  "BloodGroup",
  "State",
  "Degree",
  "Religion",
  "FileType",
  "PWD",
  "Others",
] as const;

export const OTHER_TYPE = "other";

export type MasterOpt = { type_id: string; type_text: string };
export type MasterOptionsByType = Record<string, MasterOpt[]>;

export const TYPE_ID_TEXT_MAP: Record<string, string> = {
  VH: "Visually Handicapped",
  HH: "Hearing Handicapped",
  OH: "Orthopedically Handicapped",
};

export interface MasterRecord {
  id?: number;
  type: string;
  type_id: string;
  type_text: string;
  is_active: boolean;
  created_by?: string | number | null;
  updated_by?: string | number | null;
  created_at?: string;
  modified_at?: string;
  deleted_at?: string | null;
}

export interface ApproverRecord {
  id?: number;
  moduleName: string;
  empId: string;
  designation: string;
  name: string;
  email: string;
  level: number;
  is_active: boolean;
  created_by?: string | number | null;
  updated_by?: string | number | null;
  created_at?: string;
  modified_at?: string;
  deleted_at?: string | null;
}

export interface ApplicationModule {
  id: number;
  name: string;
  slug: string;
  hindi_name?: string;
  status?: boolean;
}

export interface EmployeeOption {
  empId: string;
  name: string;
  email: string;
  designation: string;
}

function lettersOf(word: string): string {
  return String(word || "").replace(/[^a-zA-Z0-9]/g, "");
}

export function generateTypeIdFromText(typeText: string): string {
  const words = String(typeText || "")
    .trim()
    .split(/[\s\-_/,]+/)
    .map((word) => lettersOf(word))
    .filter(Boolean);

  if (!words.length) return "";

  if (words.length === 1) {
    return words[0].slice(0, TYPE_ID_MIN_LEN).toUpperCase();
  }

  let typeId = words
    .map((word) => word.charAt(0))
    .join("")
    .slice(0, TYPE_ID_MAX_LEN)
    .toUpperCase();

  if (typeId.length < TYPE_ID_MIN_LEN) {
    typeId = words[0].slice(0, TYPE_ID_MIN_LEN).toUpperCase();
  }

  return typeId.slice(0, TYPE_ID_MAX_LEN);
}

export function getTypeTextFromTypeId(typeId: string): string {
  const key = String(typeId || "").trim().toUpperCase();
  return TYPE_ID_TEXT_MAP[key] || "";
}

export function normalizeTypeId(value: string, maxLen = TYPE_ID_MAX_LEN): string {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9+\-]/g, "")
    .slice(0, maxLen);
}

export function padEmpId(empId: string): string {
  const raw = String(empId || "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return raw.padStart(8, "0");
  return raw;
}

export function unwrap(res: any): any {
  return res?.data ?? res;
}

export function extractList(res: any): any[] {
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.data?.data)) return inner.data.data;
  if (Array.isArray(inner?.items)) return inner.items;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

export function extractOne(res: any): any {
  const root = unwrap(res);
  const inner = root?.Response?.data ?? root?.response?.data ?? root?.data ?? root;
  if (Array.isArray(inner?.data)) return inner.data[0] ?? null;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    if (
      inner.data &&
      typeof inner.data === "object" &&
      !Array.isArray(inner.data)
    ) {
      return inner.data;
    }
    return inner;
  }
  return inner ?? root ?? null;
}

export function isApiSuccess(res: any): boolean {
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (root?.success === true || inner?.success === true) return true;
  if (Number(root?.status) === 200 || Number(inner?.status) === 200) return true;
  const record = extractOne(res);
  return Boolean(record?.id);
}

export function getApiMessage(res: any, fallback: string): string {
  const root = unwrap(res);
  const inner = root?.Response ?? root?.response ?? root?.data ?? root;
  return (
    root?.message ||
    inner?.message ||
    root?.data?.message ||
    unwrap(root)?.message ||
    fallback
  );
}

export async function listMasterRecords(payload: {
  type?: string;
  is_active?: boolean | null;
} = {}): Promise<MasterRecord[]> {
  const data: Record<string, any> = {
    type: payload.type ?? "",
  };
  if (payload.is_active === true || payload.is_active === false) {
    data.is_active = payload.is_active;
  }
  const res = await Api.post(API_URLS.E_ATTESTATION.MASTER_LIST, data);
  return extractList(res) as MasterRecord[];
}

export async function saveMasterRecord(
  payload: Partial<MasterRecord>
): Promise<{ success: boolean; message: string; data: MasterRecord | null }> {
  const res = await Api.post(
    API_URLS.E_ATTESTATION.MASTER_CREATE_OR_UPDATE,
    payload
  );
  return {
    success: isApiSuccess(res),
    message: getApiMessage(res, "Master record saved successfully"),
    data: extractOne(res),
  };
}

export async function toggleMasterStatus(
  id: number,
  is_active: boolean
): Promise<{ success: boolean; message: string; data: MasterRecord | null }> {
  const res = await Api.post(API_URLS.E_ATTESTATION.MASTER_TOGGLE_STATUS, {
    id,
    is_active,
  });
  return {
    success: isApiSuccess(res),
    message: getApiMessage(res, "Master status updated successfully"),
    data: extractOne(res),
  };
}

export async function fetchMasterDropdown(type: string): Promise<any[]> {
  const res = await Api.post(API_URLS.E_ATTESTATION.MASTER_DROPDOWN, { type });
  return extractList(res);
}

function mapMasterOption(row: any): MasterOpt {
  return {
    type_id: String(row?.type_id || row?.eValue || row?.id || "").trim(),
    type_text: String(row?.type_text || row?.eName || row?.label || row?.type_id || "").trim(),
  };
}

export function getMasterOptionsByType(
  byType: MasterOptionsByType,
  type: string
): MasterOpt[] {
  const key = String(type || "").trim();
  if (!key) return [];
  if (byType[key]?.length) return byType[key];
  const found = Object.keys(byType).find(
    (item) => item.toLowerCase() === key.toLowerCase()
  );
  return found ? byType[found] : [];
}

export async function fetchTypesModel(typesOnly = false): Promise<any[]> {
  const res = await Api.post(API_URLS.E_ATTESTATION.TYPES_MODEL, { typesOnly });
  return extractList(res);
}

export async function fetchTypeModels(): Promise<string[]> {
  const list = await fetchTypesModel(true);
  return (list || [])
    .map((row: any) => String(row?.type_model || row?.type || "").trim())
    .filter(Boolean);
}

let masterOptionsCache: { at: number; data: MasterOptionsByType } | null = null;

export async function fetchAllMasterOptions(
  force = false
): Promise<MasterOptionsByType> {
  if (
    !force &&
    masterOptionsCache &&
    Date.now() - masterOptionsCache.at < 60_000
  ) {
    return masterOptionsCache.data;
  }

  const groups = await fetchTypesModel(false);
  const byType: MasterOptionsByType = {};
  const looksFlat = (groups || []).some(
    (group: any) => group?.type_id && !Array.isArray(group?.data)
  );
  (groups || []).forEach((group: any) => {
    if (looksFlat) {
      const key = String(group?.type_model || group?.type || "").trim();
      if (!key) return;
      if (!byType[key]) byType[key] = [];
      const opt = mapMasterOption(group);
      if (opt.type_id || opt.type_text) byType[key].push(opt);
      return;
    }
    const key = String(group?.type_model || group?.type || "").trim();
    if (!key) return;
    const rows = Array.isArray(group?.data) ? group.data : [];
    byType[key] = rows
      .filter((row: any) => row?.is_active !== false && !row?.deleted_at)
      .map(mapMasterOption)
      .filter((row: MasterOpt) => row.type_id || row.type_text);
  });
  masterOptionsCache = { at: Date.now(), data: byType };
  return byType;
}

export async function listApprovers(payload: {
  moduleName?: string;
  is_active?: boolean | null;
} = {}): Promise<ApproverRecord[]> {
  const data: Record<string, any> = {
    moduleName: payload.moduleName || "",
  };
  if (payload.is_active === true || payload.is_active === false) {
    data.is_active = payload.is_active;
  }
  const res = await Api.post(API_URLS.E_ATTESTATION.APPROVER_LIST, data);
  return extractList(res) as ApproverRecord[];
}

export async function saveApprover(
  payload: Partial<ApproverRecord>
): Promise<{ success: boolean; message: string; data: ApproverRecord | null }> {
  const res = await Api.post(
    API_URLS.E_ATTESTATION.APPROVER_CREATE_OR_UPDATE,
    payload
  );
  return {
    success: isApiSuccess(res),
    message: getApiMessage(res, "Approver assignment saved successfully"),
    data: extractOne(res),
  };
}

export async function toggleApproverStatus(
  id: number,
  is_active: boolean
): Promise<{ success: boolean; message: string; data: ApproverRecord | null }> {
  const res = await Api.post(API_URLS.E_ATTESTATION.APPROVER_TOGGLE_STATUS, {
    id,
    is_active,
  });
  return {
    success: isApiSuccess(res),
    message: getApiMessage(res, "Approver status updated successfully"),
    data: extractOne(res),
  };
}

export async function listApplicationModules(): Promise<ApplicationModule[]> {
  const res = await Api.post(API_URLS.APPLICATION_MODULES.LIST, {
    page: 1,
    limit: 200,
    orderColumn: "name",
    orderBy: "ASC",
  });
  const rows = extractList(res).filter(
    (item: ApplicationModule) => item && (item.status === undefined || item.status)
  );
  const unique = new Map<string, ApplicationModule>();
  rows.forEach((item: ApplicationModule) => {
    const name = String(item?.name || "").trim();
    const slug = String(item?.slug || "").trim();
    const key =
      name.toLowerCase().replace(/[\s_-]+/g, "") ||
      slug.toLowerCase().replace(/[\s_-]+/g, "") ||
      String(item?.id || "");
    if (!key || unique.has(key)) return;
    unique.set(key, item);
  });
  return Array.from(unique.values()).sort((a: ApplicationModule, b: ApplicationModule) =>
    String(a.name || "").localeCompare(String(b.name || ""))
  );
}

export async function searchEmployees(query: string): Promise<EmployeeOption[]> {
  const q = String(query || "").trim();
  if (!q) return [];

  const isNum = /^\d+$/.test(q);
  const res = await Api.post(API_URLS.EMPLOYEES.FIND_ALL, {
    page: 1,
    limit: 15,
    query: q,
    filters: {
      firstname: isNum ? "" : q,
      lsastname: "",
      empId: isNum ? q : "",
      email: "",
      phone: "",
      wing: "",
      department: "",
      designation: "",
    },
  });

  const list = extractList(res);
  const mapped = list
    .map((item: any) => {
      const details = item?.details ?? item;
      const name = [
        details?.firstname,
        details?.secondname,
        details?.lsastname || details?.lastname,
      ]
        .map((part: any) => String(part || "").trim())
        .filter(Boolean)
        .join(" ");
      return {
        empId: padEmpId(
          String(details?.empId ?? item?.empId ?? item?.emp_no ?? "").trim()
        ),
        name: name || details?.fullName || details?.emp_name || "",
        email: String(details?.email || item?.email || "").trim(),
        designation: String(
          details?.designation_name ||
            details?.designation ||
            details?.designationID ||
            ""
        ).trim(),
      } as EmployeeOption;
    })
    .filter((row: EmployeeOption) => row.empId);

  if (mapped.length || !isNum) return mapped;

  try {
    const padded = padEmpId(q);
    const profileRes = await Api.post(API_URLS.EMPLOYEES.EMPLOYEE_PROFILE, {
      id: padded,
    });
    const profile = extractOne(profileRes) || unwrap(profileRes);
    const details = profile?.personal_details || profile?.details || profile;
    if (!details) return [];
    const name = [
      details?.firstname,
      details?.secondname,
      details?.lsastname || details?.lastname,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (!name && !details?.email) return [];
    return [
      {
        empId: padEmpId(String(profile?.empId || details?.empId || padded)),
        name: name || details?.fullName || details?.emp_name || "",
        email: String(details?.email || "").trim(),
        designation: String(
          details?.designation_name || details?.designation || ""
        ).trim(),
      },
    ];
  } catch {
    return mapped;
  }
}

export const YES_NO_OPTIONS = [
  { value: "NO", label: "No" },
  { value: "YES", label: "Yes" },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: "SING", label: "Single" },
  { value: "MARR", label: "Married" },
  { value: "WID", label: "Widow / Widower" },
  { value: "DIV", label: "Divorced" },
];

export const RELATION_OPTIONS = [
  "FATHER",
  "MOTHER",
  "SPOUSE",
  "SON",
  "DAUGHTER",
  "BROTHER",
  "SISTER",
];

export const APPOINTMENT_HOLDING_OPTIONS = [
  { value: "CENTRAL", label: "Under Central" },
  { value: "STATE", label: "State Government" },
  { value: "SEMI", label: "Semi Government" },
  { value: "QUASI", label: "Quasi Government" },
  { value: "AUTONOMOUS", label: "Autonomous Body" },
  { value: "NONE", label: "None" },
];

export const DEGREE_FALLBACK = [
  { type_id: "01", type_text: "Matric" },
  { type_id: "02", type_text: "Sr. Secondary" },
  { type_id: "03", type_text: "Graduation - Tech" },
  { type_id: "04", type_text: "Post Grad. - Tech" },
  { type_id: "05", type_text: "Certificate - Tech" },
  { type_id: "06", type_text: "Diploma - Tech" },
  { type_id: "07", type_text: "Doctorate - Tech" },
  { type_id: "08", type_text: "Graduation-Non Tech" },
  { type_id: "09", type_text: "Post Grad.-Non Tech" },
  { type_id: "10", type_text: "Certificate-Non Tech" },
];

export const FILE_TYPE_FALLBACK = [
  { type_id: "Photo", type_text: "Photo" },
  { type_id: "Signature", type_text: "Signature" },
  { type_id: "Identification Certificate", type_text: "Identification Certificate" },
  { type_id: "ID", type_text: "ID Proof" },
  { type_id: "AADHAR", type_text: "Aadhar Card" },
  { type_id: "VOTER", type_text: "Voter Id Card" },
  { type_id: "PASSPORT", type_text: "Passport" },
];

export const BLOOD_GROUP_FALLBACK: MasterOpt[] = [
  { type_id: "A+", type_text: "A+" },
  { type_id: "A-", type_text: "A-" },
  { type_id: "B+", type_text: "B+" },
  { type_id: "B-", type_text: "B-" },
  { type_id: "AB+", type_text: "AB+" },
  { type_id: "AB-", type_text: "AB-" },
  { type_id: "O+", type_text: "O+" },
  { type_id: "O-", type_text: "O-" },
];

export const RELIGION_FALLBACK: MasterOpt[] = [
  { type_id: "HINDU", type_text: "Hindu" },
  { type_id: "MUSLIM", type_text: "Muslim" },
  { type_id: "SIKH", type_text: "Sikh" },
  { type_id: "CHRISTIAN", type_text: "Christian" },
  { type_id: "BUDDHIST", type_text: "Buddhist" },
  { type_id: "JAIN", type_text: "Jain" },
  { type_id: "OTHER", type_text: "Other" },
];

export const NATIONALITY_OPTIONS = [
  { value: "Ind", label: "Indian" },
  { value: "Oth", label: "Others" },
];

export const ATTESTATION_INSTRUCTIONS = [
  "The furnishing of false information or suppression of any factual information in the Attestation Form would be a disqualification, and is likely to render the candidate unfit for employment under the Government.",
  "If detained, arrested, prosecuted, bound down, fined, convicted, debarred, acquitted, etc. subsequent to the completion and submission of this form, the details should be communicated immediately to the authorities to whom the attestation form has been sent earlier, failing which it will be deemed to be a suppression of factual information.",
  "If the fact that false information has been furnished or that there has been suppression of any factual information in the attestation form comes to notice at any time during the service of a person, his/her services would be liable to be terminated.",
];

export const SECTION_13_PARTICULARS =
  "If the answer to any of the above mentioned questions is Yes, give full particulars of the case / arrest / detention / fine / conviction / sentence / punishment etc. and/or the nature of the case pending in the court / university / educational authority etc. at the time of filling up this form.";

export const SECTION_13A_NOTE =
  "13(A). Please also see the 'WARNING' at the top of this Attestation Form.";

export const SECTION_13B_NOTE =
  "13(B). Specific answers to each of the questions should be given by striking out Yes or No as the case may be.";

export const MANDATORY_FILE_TYPES = [
  { value: "Photo", keys: ["PHOTO", "PH", "UP", "UPLOAD PHOTO"] },
  { value: "Signature", keys: ["SIGN", "SI", "SIGNATURE"] },
  { value: "Identification Certificate", keys: ["IDCERT", "IDENTIFICATION CERTIFICATE", "IDENT"] },
];

export function asRowList(value: any): any[] {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value || typeof value !== "object") return [];
  if (Array.isArray(value.results)) return value.results.filter(Boolean);
  if (Array.isArray(value.data)) return value.data.filter(Boolean);
  return [];
}

export function pickAttachmentContent(row: any): string {
  return String(
    pick(
      row,
      "FileContent",
      "fileContent",
      "FileContents",
      "fileContents",
      "attachmentFileContents",
      "AttachmentFileContents",
      "FileContentBase64"
    ) || ""
  );
}

export function hasUploadedAttachment(row: any): boolean {
  if (!row) return false;
  const name = String(row.fileName || row.FileName || "").trim();
  const content = String(row.fileContent || pickAttachmentContent(row) || "").trim();
  return Boolean(name || content);
}

export function resolveAttachmentFileType(
  row: { fileType?: string; fileDescription?: string },
  options: { type_id: string; type_text: string }[] = []
): string {
  const opt = options.find(
    (o) => o.type_id === row.fileType || o.type_text === row.fileType
  );
  const blob = `${row.fileType || ""} ${row.fileDescription || ""} ${opt?.type_id || ""} ${opt?.type_text || ""}`.toUpperCase();
  const matched = MANDATORY_FILE_TYPES.find((item) =>
    item.keys.some((key) => blob.includes(key))
  );
  return matched?.value || opt?.type_text || row.fileType || "";
}

export const EA_STATUS_OPTIONS = [
  { key: "", label: "All" },
  { key: "D", label: "Draft" },
  { key: "P", label: "Pending" },
  { key: "A", label: "Approved" },
  { key: "R", label: "Rejected" },
];

export const LEGAL_QUESTIONS: { key: string; legacy: string; label: string }[] = [
  { key: "arrested", legacy: "haveU", label: "12(A) Have you ever been arrested?" },
  { key: "prosecuted", legacy: "havePro", label: "12(B) Have you ever been prosecuted?" },
  { key: "detention", legacy: "deten", label: "12(C) Have you ever been kept under detention?" },
  { key: "boundDown", legacy: "boundDown", label: "12(D) Have you ever been bound down?" },
  { key: "finedByCourtOfLaw", legacy: "courtFine", label: "12(E) Have you ever been fined by a court of law?" },
  { key: "courtConviction", legacy: "noticesLaw", label: "12(F) Have you been convicted by a court of law for any offence?" },
  { key: "debarredFromExam", legacy: "autinist", label: "12(G) Have you ever been debarred from any examination or rusticated by any university or any other educational institution/authority?" },
  { key: "debarredByPublicServiceCommission", legacy: "examSelect", label: "12(H) Have you ever been debarred/disqualified by any public service commission/staff commission for any of its examination/selection?" },
  { key: "pendingCourtCaseOrFir", legacy: "bthisform", label: "12(I) Is any case pending against you in any court of law or any FIR against you at the time of filling this attestation form?" },
  { key: "pendingUniversityCase", legacy: "eduinist", label: "12(J) Is any case pending against you in any university or any other educational institution at the time of filling this attestation form?" },
  { key: "expelledByTrainingInstitution", legacy: "govOtherwise", label: "12(K) Whether discharged/expelled/withdrawn from any training institution under the government or otherwise?" },
];

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return padEmpId(
    String(user?.empId || user?.details?.empId || user?.usrid || user?.emp_id || "").trim()
  );
}

export function getEmployeeDisplayName(user = getLoggedInUser()): string {
  const details = user?.details || {};
  return (
    [details?.firstname, details?.lsastname || details?.secondname || details?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    user?.name ||
    "-"
  );
}

function isFatherRelation(value: any): boolean {
  const v = String(value ?? "").trim().toUpperCase();
  return v === "FATHER" || v === "FATH" || v === "11";
}

function familyMemberName(member: any): string {
  if (!member || typeof member !== "object") return "";
  const joined = [
    pick(member, "firstname", "firstName", "FirstName"),
    pick(member, "middlename", "middleName", "MiddleName"),
    pick(member, "lastname", "lastName", "LastName", "lsastname"),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  return String(
    joined ||
      pick(member, "name", "Name", "fullName", "FullName", "father_name", "fatherName") ||
      ""
  ).trim();
}

export function collectFamilyMembers(obj: any): any[] {
  if (!obj) return [];
  const candidates = [
    obj.familyMembers,
    obj.family_members,
    obj.FamilyMembers,
    obj.details?.familyMembers,
    obj.details?.family_members,
    obj.personal_details?.familyMembers,
    obj.data?.familyMembers,
    obj.data?.data?.familyMembers,
    obj.data?.data?.data?.familyMembers,
  ];
  for (const list of candidates) {
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
}

export function getFatherNameFromSource(source: any): string {
  const members = collectFamilyMembers(source);
  const father = members.find((row: any) =>
    isFatherRelation(
      pick(row, "relation", "Relation", "relationType", "RelationType", "relationship")
    )
  );
  const fromMembers = familyMemberName(father);
  if (fromMembers) return fromMembers;
  const details = source?.details || source?.personal_details || source || {};
  return String(
    pick(
      details,
      "father_name",
      "fatherName",
      "fathers_name",
      "FatherName",
      "FATHER_NAME"
    ) ||
      pick(source, "father_name", "fatherName", "FatherName") ||
      ""
  ).trim();
}

export function getFatherNameFromLoggedInProfile(): string {
  return getFatherNameFromSource(getLoggedInUser());
}

export function getFatherNameFromFamilyRows(rows: any[]): string {
  if (!Array.isArray(rows)) return "";
  const father = rows.find((row: any) =>
    isFatherRelation(pick(row, "relationType", "RelationType", "relation", "Relation"))
  );
  return familyMemberName(father);
}

export async function fetchFatherNameFromMyProfile(empId?: string): Promise<string> {
  const fromLocal = getFatherNameFromLoggedInProfile();
  const selfId = getLoggedInEmpId();
  const targetId = padEmpId(String(empId || selfId || "").trim());
  const isSelf = !targetId || !selfId || targetId === selfId;

  if (isSelf) {
    try {
      const res: any = await Api.post(API_URLS.EMPLOYEES.My_PROFILE, {});
      const root = unwrap(res);
      const profile = root?.data?.data ?? root?.data ?? root;
      const name = getFatherNameFromSource(profile) || getFatherNameFromSource(root);
      if (name) return name;
    } catch {
      /* fall through */
    }
    if (fromLocal) return fromLocal;
  }

  if (!targetId) return fromLocal;

  try {
    const res: any = await Api.post(API_URLS.EMPLOYEES.EMPLOYEE_PROFILE, { id: targetId });
    const profile = extractOne(res) || unwrap(res);
    return (
      getFatherNameFromSource(profile) ||
      getFatherNameFromSource(unwrap(res)) ||
      fromLocal
    );
  } catch {
    return fromLocal;
  }
}

export type ToReferenceItem = {
  referenceNo: number;
  referenceName: string;
  referenceAddress: string;
  referenceMobileNo: string;
};

export function getToReferences(data: any): ToReferenceItem[] {
  const list = data?.toReference || data?.ToReference || [];
  if (Array.isArray(list) && list.length) {
    return list.map((row: any, idx: number) => ({
      referenceNo: Number(pick(row, "referenceNo", "ReferenceNo")) || idx + 1,
      referenceName: String(pick(row, "referenceName", "ReferenceName") || "").trim(),
      referenceAddress: String(
        pick(row, "referenceAddress", "ReferenceAddress") || ""
      ).trim(),
      referenceMobileNo: String(
        pick(row, "referenceMobileNo", "ReferenceMobileNo") || ""
      ).trim(),
    }));
  }
  return [
    {
      referenceNo: 1,
      referenceName: String(pick(data, "Reference1Name", "reference1Name") || "").trim(),
      referenceAddress: String(
        pick(data, "Reference1Address", "reference1Address") || ""
      ).trim(),
      referenceMobileNo: String(
        pick(data, "Reference1MobileNo", "reference1MobileNo") || ""
      ).trim(),
    },
    {
      referenceNo: 2,
      referenceName: String(pick(data, "Reference2Name", "reference2Name") || "").trim(),
      referenceAddress: String(
        pick(
          data,
          "Reference2Addresss",
          "reference2Addresss",
          "Reference2Address",
          "reference2Address"
        ) || ""
      ).trim(),
      referenceMobileNo: String(
        pick(data, "Reference2MobileNo", "reference2MobileNo") || ""
      ).trim(),
    },
  ];
}

export function pick(obj: any, ...keys: string[]): any {
  if (!obj || typeof obj !== "object") return "";
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
    const found = Object.keys(obj).find(
      (k) => k.toLowerCase() === String(key).toLowerCase()
    );
    if (found && obj[found] !== undefined && obj[found] !== null && obj[found] !== "") {
      return obj[found];
    }
  }
  return "";
}

/** SAP request number used by find-one (`reqId`), kept as a string so leading zeros survive. */
export function getEAttestationReqId(source: any): string {
  if (source == null || source === "") return "";
  if (typeof source !== "object") return String(source).trim();
  return String(
    pick(
      source,
      "Reqid",
      "reqid",
      "reqId",
      "ReqId",
      "requestID",
      "RequestID",
      "requestId"
    ) || ""
  ).trim();
}

export function toYesNo(value: any, fallback = "NO"): string {
  const v = String(value ?? "").trim().toUpperCase();
  if (["Y", "YES", "1", "TRUE"].includes(v)) return "YES";
  if (["N", "NO", "0", "FALSE"].includes(v)) return "NO";
  return v || fallback;
}

export function toInputDate(value: any): string {
  if (value === undefined || value === null || value === "") return "";
  const str = String(value).trim();
  if (!str || str === "null") return "";
  const ms = str.match(/\/Date\((-?\d+)\)\//);
  if (ms) {
    const date = new Date(parseInt(ms[1], 10));
    if (isNaN(date.getTime())) return "";
    return toYmd(date);
  }
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = str.match(/^(\d{2})[./-](\d{2})[./-](\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const date = new Date(str);
  if (!isNaN(date.getTime())) return toYmd(date);
  return "";
}

export function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function toSapDate(value: any): string {
  if (value === undefined || value === null || value === "") return "";
  const str = String(value).trim();
  if (str.includes("/Date(")) return str;
  const input = toInputDate(str);
  if (!input) return "";
  const date = new Date(`${input}T00:00:00`);
  if (isNaN(date.getTime())) return "";
  return `/Date(${date.getTime()})/`;
}

export function toDisplayDate(value: any): string {
  const input = toInputDate(value);
  if (!input) return "-";
  const [y, m, d] = input.split("-");
  return `${d}.${m}.${y}`;
}

export function calcPresentAge(dob: any): string {
  const input = toInputDate(dob);
  if (!input) return "";
  const birth = new Date(`${input}T00:00:00`);
  if (isNaN(birth.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age -= 1;
  return String(Math.max(0, age)).padStart(3, "0");
}

export function formatPresentAge(value: any): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  const years = Number(digits);
  return `${years} year${years === 1 ? "" : "s"}`;
}

export function genderLabel(value: any): string {
  const raw = String(value || "").trim().toUpperCase();
  if (["1", "M", "MALE"].includes(raw)) return "Male";
  if (["2", "F", "FEMALE"].includes(raw)) return "Female";
  if (["3", "O", "OTHER", "OTHERS"].includes(raw)) return "Other";
  return String(value || "").trim();
}

export function genderPayloadValue(value: any): string {
  const label = genderLabel(value);
  return label ? label.toUpperCase() : "";
}

export function nationalityValue(value: any): string {
  const raw = String(value || "").trim();
  if (!raw) return "Ind";
  const upper = raw.toUpperCase();
  if (["IN", "IND", "INDIAN"].includes(upper)) return "Ind";
  if (["OTH", "OTHER", "OTHERS"].includes(upper)) return "Oth";
  const found = NATIONALITY_OPTIONS.find(
    (item) =>
      item.value.toLowerCase() === raw.toLowerCase() ||
      item.label.toLowerCase() === raw.toLowerCase()
  );
  return found?.value || raw;
}

export function nationalityLabel(value: any): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const resolved = nationalityValue(raw);
  return (
    NATIONALITY_OPTIONS.find((item) => item.value === resolved)?.label || raw
  );
}

export function resolveMasterValue(options: MasterOpt[], raw: any): string {
  const value = String(raw || "").trim();
  if (!value) return "";
  const found = options.find(
    (item) =>
      String(item.type_id || "").toLowerCase() === value.toLowerCase() ||
      String(item.type_text || "").toLowerCase() === value.toLowerCase()
  );
  return found?.type_id || value;
}

export function masterSelectOptions(
  options: MasterOpt[]
): { value: string; label: string }[] {
  return options
    .filter((item) => item.type_id || item.type_text)
    .map((item) => ({
      value: item.type_id || item.type_text,
      label: item.type_text || item.type_id,
    }));
}

export function padAge(value: any): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.slice(0, 3).padStart(3, "0");
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function getFileMime(file?: File, fileName?: string): string {
  if (file?.type) return file.type;
  const name = String(fileName || file?.name || "").toLowerCase();
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  return "application/octet-stream";
}

export function downloadBase64File(content: string, fileName: string, mimeType?: string) {
  if (!content) return;
  const mime = mimeType || "application/octet-stream";
  const raw = content.includes(",") ? content.split(",")[1] : content;
  const link = document.createElement("a");
  link.href = `data:${mime};base64,${raw}`;
  link.download = fileName || "document";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function viewBase64File(content: string, fileName: string, mimeType?: string) {
  if (!content) return;
  const mime = mimeType || getFileMime(undefined, fileName);
  const raw = content.includes(",") ? content.split(",")[1] : content;
  try {
    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    downloadBase64File(content, fileName, mime);
  }
}

export function getStatusCode(item: any): string {
  if (item?.Is_draft === true || item?.is_draft === true || item?.Draft === true || item?.draft === true) {
    return "D";
  }
  const raw = String(
    pick(item, "RequestStatus", "Status", "status", "requestStatus") || ""
  ).trim().toUpperCase();
  if (["D", "DRAFT"].includes(raw)) return "D";
  if (["A", "APPROVED"].includes(raw)) return "A";
  if (["R", "REJECTED"].includes(raw)) return "R";
  if (["P", "T", "N", "B", "PENDING"].includes(raw)) return "P";
  const text = String(pick(item, "StatusText", "StatusDescription") || "").toLowerCase();
  if (text.includes("draft")) return "D";
  if (text.includes("reject")) return "R";
  if (text.includes("approv")) return "A";
  return raw || "P";
}

export function getApproverDisplay(item: any): string {
  const name = String(pick(item, "ApproverName", "approverName") || "").trim();
  const idRaw = String(
    pick(
      item,
      "ApproverUserID",
      "approverUserID",
      "ApproverId",
      "approverId",
      "ApproverEmpId",
      "approverEmpId",
      "ApproverEmpID"
    ) || ""
  ).trim();
  const id = padEmpId(idRaw);
  if (name && id) return `${name} (${id})`;
  if (name) return name;
  if (id) return id;
  return "-";
}

export function getStatusLabel(item: any): string {
  const text = String(pick(item, "StatusText", "StatusDescription") || "").trim();
  if (text) return text;
  const code = getStatusCode(item);
  if (code === "D") return "Draft";
  if (code === "A") return "Approved";
  if (code === "R") return "Rejected";
  if (code === "P") return "Pending";
  return code || "-";
}

export function getStatusBadgeClass(item: any): string {
  const code = getStatusCode(item);
  if (code === "A") return "badge bg-success";
  if (code === "R") return "badge bg-danger";
  if (code === "D") return "badge bg-info text-dark";
  return "badge bg-warning text-dark";
}

export function isDraftRequest(item: any): boolean {
  return getStatusCode(item) === "D";
}

export function isRejectedRequest(item: any): boolean {
  return getStatusCode(item) === "R";
}

export function isApprovedRequest(item: any): boolean {
  return getStatusCode(item) === "A";
}

export function hasAnyApproverApproved(item: any): boolean {
  if (isApprovedRequest(item)) return true;
  const level = Number(pick(item, "ApproverLevel", "approverLevel") || 0);
  if (level > 1) return true;
  const remarks = item?.Remarks_data || item?.remarks_data || item?.approvalLog || [];
  if (Array.isArray(remarks) && remarks.some((row: any) => {
    const action = String(pick(row, "action", "Action", "status", "Status") || "").toUpperCase();
    return action === "A" || action === "APPROVED";
  })) {
    return true;
  }
  const status = String(pick(item, "RequestStatus", "Status") || "").toUpperCase();
  return status === "P" && level > 1;
}

export function canEditRequest(item: any): boolean {
  return isDraftRequest(item) || isRejectedRequest(item);
}

export function canDeleteRequest(item: any): boolean {
  if (!item) return false;
  if (isRejectedRequest(item)) return false;
  if (isApprovedRequest(item)) return false;
  if (hasAnyApproverApproved(item)) return false;
  return isDraftRequest(item) || getStatusCode(item) === "P";
}

export function canCreateNewRequest(items: any[]): boolean {
  return !(items || []).some((item) => item && !pick(item, "Deleted_at", "deleted_at"));
}

export function isEAttestationWorklistItem(task: {
  type?: string;
  taskText?: string;
  workItemText?: string;
  subject?: string;
}): boolean {
  const type = String(task?.type || "").toUpperCase();
  if (
    type === "E_ATTESTATION" ||
    type === "EATTESTATION" ||
    type.includes("E-ATTESTATION") ||
    type.includes("E_ATTESTATION")
  ) {
    return true;
  }
  const text = `${task?.taskText || ""} ${task?.workItemText || ""} ${task?.subject || ""}`.toLowerCase();
  return text.includes("e-attestation") || text.includes("e attestation") || text.includes("attestation form");
}

export function isEaActionable(item: any): boolean {
  if (!item) return false;
  if (isDraftRequest(item) || isRejectedRequest(item) || isApprovedRequest(item)) return false;
  return true;
}

export async function fetchMasterOptions(type: string): Promise<MasterOpt[]> {
  try {
    const byType = await fetchAllMasterOptions();
    const fromTypesModel = getMasterOptionsByType(byType, type);
    if (fromTypesModel.length) return fromTypesModel;
  } catch {
    /* fall through */
  }
  try {
    const list = await listMasterRecords({ type, is_active: true });
    const mapped = (list || [])
      .filter((row) => row?.is_active !== false)
      .map(mapMasterOption)
      .filter((row) => row.type_id || row.type_text);
    if (mapped.length) return mapped;
  } catch {
    /* fall through */
  }
  try {
    const dropdown = await fetchMasterDropdown(type);
    return (dropdown || [])
      .map(mapMasterOption)
      .filter((row: MasterOpt) => row.type_id || row.type_text);
  } catch {
    return [];
  }
}

export function buildEAttestationEnvelope(data: Record<string, any>, endpoint: string) {
  let path = endpoint;
  try {
    path = new URL(endpoint).pathname;
  } catch {
    path = endpoint;
  }
  return {
    Header: {
      device_id: "WEB",
      device_token: "WEB",
      device_type: "WEB",
      guid:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      epoch: Date.now(),
      url: path,
      service_request_id: path,
    },
    Request: {
      data,
    },
  };
}

const PG_INT_MAX = 2147483647;

export function isEAttestationDbId(value: string | number): boolean {
  const raw = String(value ?? "").trim();
  if (!raw || !/^\d+$/.test(raw)) return false;
  if (raw.length > 1 && raw.startsWith("0")) return false;
  if (raw.length > 9) return false;
  const num = Number(raw);
  return Number.isInteger(num) && num > 0 && num <= PG_INT_MAX;
}

function isFailedApiResponse(res: any): boolean {
  const root = unwrap(res);
  const inner = root?.Response ?? root?.response ?? root?.data ?? root;
  return (
    root?.success === false ||
    inner?.success === false ||
    Number(root?.status) >= 400 ||
    Number(inner?.status) >= 400
  );
}

export async function findAllEAttestationRequests(payload: {
  page?: number;
  limit?: number;
  status?: string;
} = {}): Promise<{ list: any[]; total: number }> {
  const res: any = await Api.post(API_URLS.E_ATTESTATION.REQUEST_FIND_ALL, {
    page: payload.page || 1,
    limit: payload.limit || 50,
    status: payload.status || "",
  });
  const list = extractList(res);
  const root = unwrap(res);
  const inner = root?.data ?? root;
  const total = Number(inner?.total ?? inner?.data?.total ?? list.length) || list.length;
  return { list, total };
}

export async function findOneEAttestationRequest(id: string | number): Promise<any> {
  const reqId = getEAttestationReqId(id);
  if (!reqId) return null;
  const res: any = await Api.post(
    API_URLS.E_ATTESTATION.REQUEST_FIND_ONE,
    buildEAttestationEnvelope(
      { reqId },
      API_URLS.E_ATTESTATION.REQUEST_FIND_ONE
    ),
    true
  );
  if (isFailedApiResponse(res)) {
    throw new Error(getApiMessage(res, "Failed to load request details"));
  }
  return extractOne(res);
}

const FORBIDDEN_REQUEST_ROOT_KEYS = [
  "serialNo",
  "fileType",
  "subFileType",
  "attachmentFileName",
  "attachmentFileType",
  "attachmentFileContents",
];

function sanitizeEAttestationRequestPayload(payload: Record<string, any> = {}) {
  const data = { ...payload };
  FORBIDDEN_REQUEST_ROOT_KEYS.forEach((key) => {
    delete data[key];
  });
  return data;
}

export async function createOrUpdateEAttestation(payload: any): Promise<any> {
  const res: any = await Api.post(
    API_URLS.E_ATTESTATION.REQUEST_CREATE_OR_UPDATE,
    buildEAttestationEnvelope(
      sanitizeEAttestationRequestPayload(payload),
      API_URLS.E_ATTESTATION.REQUEST_CREATE_OR_UPDATE
    ),
    true
  );
  const root = unwrap(res);
  const inner = root?.data ?? root;
  const failed =
    root?.success === false ||
    inner?.success === false ||
    Number(root?.status) >= 400 ||
    Number(inner?.status) >= 400;
  if (failed) {
    throw new Error(getApiMessage(res, "Failed to save E-Attestation request"));
  }
  return res;
}

export async function deleteEAttestationRequest(id: string | number): Promise<any> {
  const res: any = await Api.post(API_URLS.E_ATTESTATION.REQUEST_DELETE, { id: Number(id) || id });
  if (!isApiSuccess(res) && res?.success === false) {
    throw new Error(getApiMessage(res, "Failed to delete request"));
  }
  return res;
}

export async function approveRejectEAttestation(payload: {
  id: string | number;
  action: "A" | "R";
  remarks: string;
}): Promise<any> {
  const res: any = await Api.post(
    API_URLS.E_ATTESTATION.REQUEST_APPROVE_REJECT,
    buildEAttestationEnvelope(
      {
        id: isEAttestationDbId(payload.id) ? Number(payload.id) : payload.id,
        action: payload.action,
        remarks: payload.remarks || "",
      },
      API_URLS.E_ATTESTATION.REQUEST_APPROVE_REJECT
    ),
    true
  );
  if (!isApiSuccess(res) && unwrap(res)?.success === false) {
    throw new Error(getApiMessage(res, "Action failed"));
  }
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (root?.success === false || inner?.success === false || Number(root?.status) >= 400) {
    throw new Error(getApiMessage(res, "Action failed"));
  }
  return res;
}
